import { getBackendUrl } from '@/lib/backendUrl';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  ArrowLeft, FolderTree, Plus, Pencil, Trash2, Lock,
  ChevronDown, ChevronRight, FolderPlus, FolderOpen, AlertTriangle,
  Package, ArrowRightLeft, Search, Check, ShieldAlert, History,
} from 'lucide-react';
import axios from 'axios';
import { ProductMasterSnagsPanel } from '../components/ProductMasterSnagsPanel';

const API = `${getBackendUrl()}/api`;

function authHeaders() {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// ── Code Range Audit Panel ─────────────────────────────────────────────────
function CodeRangeAuditPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [subcatFilter, setSubcatFilter] = useState('');
  const [fixDialog, setFixDialog] = useState(null); // { item, dryRunResult, stage: 'dry'|'apply'|'done' }
  const [fixLoading, setFixLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/product-taxonomy/code-range-audit`, { headers: authHeaders() });
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load code range audit');
    } finally {
      setLoading(false);
    }
  };

  const filteredIssues = useMemo(() => {
    if (!data) return [];
    const kw = subcatFilter.trim().toLowerCase();
    return kw
      ? data.issues.filter(i => i.subcategory_name.toLowerCase().includes(kw) || i.product_name.toLowerCase().includes(kw))
      : data.issues;
  }, [data, subcatFilter]);

  // Step 1: open fix dialog with dry-run preview
  const openFix = async (item) => {
    if (!item.proposed_code) { toast.error('No available code slot in range for this subcategory.'); return; }
    setFixLoading(true);
    try {
      const r = await axios.post(`${API}/product-taxonomy/code-range-fix`,
        { product_id: item.bp_id, proposed_code: item.proposed_code, dry_run: true },
        { headers: authHeaders() });
      setFixDialog({ item, dryRunResult: r.data, stage: 'dry' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dry-run failed');
    } finally {
      setFixLoading(false);
    }
  };

  // Step 2: explicit apply
  const applyFix = async () => {
    if (!fixDialog) return;
    setFixLoading(true);
    try {
      const r = await axios.post(`${API}/product-taxonomy/code-range-fix`,
        { product_id: fixDialog.item.bp_id, proposed_code: fixDialog.dryRunResult.proposed_code, dry_run: false },
        { headers: authHeaders() });
      if (r.data.applied) {
        setFixDialog(d => ({ ...d, dryRunResult: r.data, stage: 'done' }));
        toast.success(`Code fixed: ${r.data.current_code} → ${r.data.proposed_code}`);
        load(); // Refresh audit data
      } else {
        toast.error(r.data.conflict_detail || 'Apply failed — conflict detected');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed');
    } finally {
      setFixLoading(false);
    }
  };

  return (
    <>
    <Card className="border-amber-200 bg-amber-50/30" data-testid="code-range-audit-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-amber-600" />
            <CardTitle className="text-base font-semibold text-amber-900">Product Master — Code Range Audit</CardTitle>
            {data && (
              <Badge variant="destructive" className="text-xs" data-testid="wrong-code-count-badge">
                {data.summary.wrong_code_count} wrong-code
              </Badge>
            )}
            {data && (
              <Badge className="text-xs bg-slate-200 text-slate-700" data-testid="no-audit-count-badge">
                {data.summary.no_audit_count} no audit trail
              </Badge>
            )}
            {data && (
              <Badge className={`text-xs ${data.summary.production_heal_status === 'CONFIRMED' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}
                     data-testid="production-heal-status-badge">
                Prod heal: {data.summary.production_heal_status}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="code-range-audit-run">
              {loading ? 'Running…' : 'Run Audit'}
            </Button>
            {data && (
              <Button size="sm" variant="ghost" onClick={() => setExpanded(e => !e)} data-testid="code-range-expand">
                {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
            )}
          </div>
        </div>
        <p className="text-xs text-amber-700 mt-1">
          Identifies products with codes outside their subcategory range. Each row shows a proposed in-range code. Use <strong>Fix Code</strong> to dry-run then apply (admin only). Production heal status: {data ? <span className="font-medium">{data.summary.production_heal_status}</span> : 'not loaded'} — no audit_log source 'PRODUCTION_HEAL' found; cannot confirm a prior Production heal.
        </p>
      </CardHeader>

      {data && expanded && (
        <CardContent className="pt-0 space-y-4">
          {/* Summary by subcategory */}
          <div>
            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2 flex items-center gap-1">
              <ShieldAlert className="h-3.5 w-3.5 text-amber-500" /> By Subcategory ({data.summary.by_subcategory.length})
            </h4>
            <div className="rounded border bg-white overflow-hidden text-xs">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-3 py-2">Subcategory</th>
                    <th className="text-right px-3 py-2">Expected range</th>
                    <th className="text-right px-3 py-2">Wrong-code</th>
                    <th className="text-right px-3 py-2">No audit trail</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.summary.by_subcategory.map(s => (
                    <tr key={s.subcategory_name} className="hover:bg-slate-50"
                        data-testid={`cra-subcat-${s.subcategory_id}`}>
                      <td className="px-3 py-1.5 font-medium">{s.subcategory_name}</td>
                      <td className="px-3 py-1.5 text-right text-slate-500">{s.code_min}–{s.code_max}</td>
                      <td className="px-3 py-1.5 text-right text-red-600 font-semibold">{s.count}</td>
                      <td className="px-3 py-1.5 text-right text-amber-600">{s.without_audit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Per-product detail with proposed code + Fix button */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide flex items-center gap-1">
                <History className="h-3.5 w-3.5 text-slate-400" /> Individual Products ({filteredIssues.length} of {data.issues.length})
              </h4>
              <Input
                className="h-7 text-xs w-52"
                placeholder="Filter by name or subcategory…"
                value={subcatFilter}
                onChange={e => setSubcatFilter(e.target.value)}
                data-testid="cra-filter-input"
              />
            </div>
            <div className="rounded border bg-white max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2">BP ID</th>
                    <th className="text-left px-3 py-2">Product Name</th>
                    <th className="text-right px-3 py-2">Current code</th>
                    <th className="text-right px-3 py-2 text-amber-700">Proposed code</th>
                    <th className="text-left px-3 py-2">Subcategory</th>
                    <th className="text-right px-3 py-2">Expected range</th>
                    <th className="text-center px-3 py-2">Audit trail</th>
                    <th className="text-center px-3 py-2">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredIssues.map(item => (
                    <tr key={item.bp_id} className={`hover:bg-slate-50 ${!item.has_audit_entry ? 'bg-amber-50/60' : ''}`}
                        data-testid={`cra-item-${item.bp_id}`}>
                      <td className="px-3 py-1 text-slate-400">{item.bp_id}</td>
                      <td className="px-3 py-1 font-medium max-w-[180px] truncate" title={item.product_name}>{item.product_name}</td>
                      <td className="px-3 py-1 text-right font-mono text-red-600">{item.product_code}</td>
                      <td className="px-3 py-1 text-right font-mono text-green-700 font-semibold" data-testid={`cra-proposed-${item.bp_id}`}>
                        {item.proposed_code ?? <span className="text-slate-400 font-normal">range full</span>}
                      </td>
                      <td className="px-3 py-1 text-slate-600">{item.subcategory_name}</td>
                      <td className="px-3 py-1 text-right text-slate-500">{item.code_min}–{item.code_max}</td>
                      <td className="px-3 py-1 text-center">
                        {item.has_audit_entry
                          ? <Badge className="text-xs bg-green-100 text-green-700 px-1.5 py-0">Yes</Badge>
                          : <Badge className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0">Missing</Badge>}
                      </td>
                      <td className="px-3 py-1 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 text-xs px-2 border-amber-300 hover:bg-amber-50"
                          disabled={!item.proposed_code || fixLoading}
                          onClick={() => openFix(item)}
                          data-testid={`cra-fix-btn-${item.bp_id}`}
                        >
                          Fix Code
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {filteredIssues.length === 0 && (
                    <tr><td colSpan={8} className="px-3 py-4 text-center text-slate-400">No issues found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-400 mt-1.5">
              Proposed code = next available slot in the subcategory's governed range. "Fix Code" opens a dry-run preview; admin confirmation required to apply. Scheduler role may view dry-run but cannot apply.
            </p>
          </div>
        </CardContent>
      )}
    </Card>

    {/* Code Fix Dry-run / Apply Dialog */}
    {fixDialog && (
      <Dialog open={!!fixDialog} onOpenChange={() => { if (!fixLoading) setFixDialog(null); }}>
        <DialogContent className="max-w-md" data-testid="code-fix-dialog">
          <DialogHeader>
            <DialogTitle>
              {fixDialog.stage === 'done' ? 'Code Fix Applied' : 'Fix Product Code — Dry-run Preview'}
            </DialogTitle>
            <DialogDescription>
              {fixDialog.stage === 'done'
                ? 'The product code has been updated and an audit trail entry written.'
                : 'Review the proposed change. No writes occur until you click "Apply Fix".'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            <div className="rounded-md bg-slate-50 border p-3 space-y-2">
              <div className="flex justify-between">
                <span className="text-slate-500">Product</span>
                <span className="font-medium">{fixDialog.dryRunResult.product_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">BP ID</span>
                <span className="font-mono">{fixDialog.dryRunResult.product_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Subcategory</span>
                <span>{fixDialog.dryRunResult.subcategory_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Governed range</span>
                <span className="font-mono">{fixDialog.dryRunResult.code_min}–{fixDialog.dryRunResult.code_max}</span>
              </div>
              <div className="border-t pt-2 flex justify-between items-center">
                <span className="text-slate-500">Current code</span>
                <span className="font-mono text-red-600 font-semibold">{fixDialog.dryRunResult.current_code}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Proposed code</span>
                <span className="font-mono text-green-700 font-semibold">{fixDialog.dryRunResult.proposed_code}</span>
              </div>
            </div>
            {fixDialog.dryRunResult.conflict && (
              <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700" data-testid="code-fix-conflict">
                Conflict: {fixDialog.dryRunResult.conflict_detail}
              </div>
            )}
            {fixDialog.stage === 'done' && (
              <div className="rounded-md border border-green-200 bg-green-50 p-2 text-xs text-green-700" data-testid="code-fix-done">
                Applied. Audit trail source: ADMIN_CODE_FIX. Run Audit again to confirm removal from wrong-code list.
              </div>
            )}
            {fixDialog.stage === 'dry' && !fixDialog.dryRunResult.conflict && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800" data-testid="code-fix-dryrun-note">
                Dry-run only — no writes performed yet. Click "Apply Fix" to commit. An ADMIN_CODE_FIX audit trail entry will be written.
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFixDialog(null)} disabled={fixLoading}>
              {fixDialog.stage === 'done' ? 'Close' : 'Cancel'}
            </Button>
            {fixDialog.stage === 'dry' && !fixDialog.dryRunResult.conflict && (
              <Button
                onClick={applyFix}
                disabled={fixLoading}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="code-fix-apply-btn"
              >
                {fixLoading ? 'Applying…' : 'Apply Fix'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )}
    </>
  );
}


export default function ProductCategoriesAdminPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [editingCat, setEditingCat] = useState(null);
  const [editingSub, setEditingSub] = useState(null);
  const [creatingCat, setCreatingCat] = useState(false);
  const [creatingSubFor, setCreatingSubFor] = useState(null); // category obj
  const [confirmDelete, setConfirmDelete] = useState(null);   // { kind, id, label, blocker? }
  // iter354b — product reassignment dialog state
  // { sourceCategory, sourceSubcategory: obj|null, sourceLabel } — null sub = "unassigned within category"
  const [manageProducts, setManageProducts] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/product-taxonomy/categories`, {
        params: { include_inactive: includeInactive },
        headers: authHeaders(),
      });
      setCategories(r.data?.categories || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [includeInactive]);

  const totals = useMemo(() => {
    const cat = categories.length;
    let sub = 0; let prod = 0;
    categories.forEach(c => {
      sub += (c.subcategories || []).length;
      prod += c.product_count || 0;
    });
    return { cat, sub, prod };
  }, [categories]);

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const handleSaveCat = async (payload, existing) => {
    try {
      if (existing) {
        await axios.patch(`${API}/product-taxonomy/categories/${existing.id}`, payload, { headers: authHeaders() });
        toast.success('Category updated');
      } else {
        await axios.post(`${API}/product-taxonomy/categories`, payload, { headers: authHeaders() });
        toast.success('Category created');
      }
      setCreatingCat(false); setEditingCat(null);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const handleSaveSub = async (payload, existing) => {
    try {
      if (existing) {
        await axios.patch(`${API}/product-taxonomy/subcategories/${existing.id}`, payload, { headers: authHeaders() });
        toast.success('Subcategory updated');
      } else {
        await axios.post(`${API}/product-taxonomy/subcategories`, payload, { headers: authHeaders() });
        toast.success('Subcategory created');
      }
      setCreatingSubFor(null); setEditingSub(null);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    const url = confirmDelete.kind === 'category'
      ? `${API}/product-taxonomy/categories/${confirmDelete.id}`
      : `${API}/product-taxonomy/subcategories/${confirmDelete.id}`;
    try {
      await axios.delete(url, { headers: authHeaders() });
      toast.success(`${confirmDelete.label} deleted`);
      setConfirmDelete(null);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="product-categories-admin">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/stocktaking/dashboard')}
          data-testid="back-to-stocktaking">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FolderTree className="h-6 w-6 text-sky-600" />
            Product Categories
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage main categories and subcategories. Renames flow into live products but historical stocktake reports remain locked to their original values.
          </p>
        </div>
        <Button onClick={() => setCreatingCat(true)} data-testid="add-category-btn">
          <FolderPlus className="h-4 w-4 mr-2" />New Category
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Categories</p>
          <p className="text-2xl font-semibold" data-testid="stat-categories">{totals.cat}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Subcategories</p>
          <p className="text-2xl font-semibold" data-testid="stat-subcategories">{totals.sub}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Total products mapped</p>
          <p className="text-2xl font-semibold" data-testid="stat-products">{totals.prod}</p>
        </CardContent></Card>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Switch
            id="show-inactive"
            checked={includeInactive}
            onCheckedChange={setIncludeInactive}
            data-testid="toggle-inactive"
          />
          <Label htmlFor="show-inactive" className="text-sm">Show deactivated</Label>
        </div>
      </div>

      {/* Tree */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Taxonomy tree</CardTitle>
          <CardDescription>
            Built-in system categories ({categories.filter(c => c.is_system).length}) cannot be deleted but can be renamed or deactivated.
            Custom categories appear alongside them.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
          ) : categories.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">No categories yet.</div>
          ) : (
            <div className="space-y-1">
              {categories.map(cat => (
                <CategoryRow
                  key={cat.id}
                  cat={cat}
                  expanded={!!expanded[cat.id]}
                  onToggle={() => toggleExpand(cat.id)}
                  onEdit={() => setEditingCat(cat)}
                  onDelete={() => setConfirmDelete({
                    kind: 'category', id: cat.id, label: cat.name,
                    blocker: cat.is_system
                      ? 'System categories cannot be deleted.'
                      : cat.product_count > 0
                        ? `${cat.product_count} product(s) still assigned — reassign them first.`
                        : null,
                  })}
                  onAddSub={() => setCreatingSubFor(cat)}
                  onEditSub={(sub) => setEditingSub({ sub, parent: cat })}
                  onDeleteSub={(sub) => setConfirmDelete({
                    kind: 'subcategory', id: sub.id,
                    label: `${cat.name} → ${sub.name}`,
                    blocker: sub.product_count > 0
                      ? `${sub.product_count} product(s) still assigned — reassign them first.`
                      : null,
                  })}
                  onManageProducts={(sub) => setManageProducts({
                    sourceCategory: cat,
                    sourceSubcategory: sub, // null = unassigned-within-category bucket
                    sourceLabel: sub ? `${cat.name} → ${sub.name}` : `${cat.name} → (unassigned)`,
                  })}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / edit category dialog */}
      <CategoryDialog
        open={creatingCat || !!editingCat}
        existing={editingCat}
        onClose={() => { setCreatingCat(false); setEditingCat(null); }}
        onSave={handleSaveCat}
      />

      {/* Create / edit subcategory dialog */}
      <SubcategoryDialog
        open={!!creatingSubFor || !!editingSub}
        parentCategory={creatingSubFor || editingSub?.parent}
        allCategories={categories}
        existing={editingSub?.sub}
        onClose={() => { setCreatingSubFor(null); setEditingSub(null); }}
        onSave={handleSaveSub}
      />

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Delete {confirmDelete?.kind === 'category' ? 'category' : 'subcategory'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.blocker ? (
                <span className="text-red-600 font-medium">{confirmDelete.blocker}</span>
              ) : (
                <>
                  <strong>{confirmDelete?.label}</strong> will be permanently removed.
                  Historical stocktake reports referencing this entry are unaffected (they keep a snapshot of the original value).
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Cancel</AlertDialogCancel>
            {!confirmDelete?.blocker && (
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete"
              >
                Delete
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manage / reassign products dialog (iter354b) */}
      <ManageProductsDialog
        open={!!manageProducts}
        source={manageProducts}
        allCategories={categories}
        onClose={() => setManageProducts(null)}
        onDone={async () => { setManageProducts(null); await refresh(); }}
      />

      {/* Code Range Audit Panel */}
      <CodeRangeAuditPanel />
      <ProductMasterSnagsPanel />
    </div>
  );
}

// ── Row component ─────────────────────────────────────────────────

function CategoryRow({ cat, expanded, onToggle, onEdit, onDelete, onAddSub, onEditSub, onDeleteSub, onManageProducts }) {
  // iter354b — "unassigned" bucket = products inside this category but with NO subcategory_id.
  // Inferred from category-level vs sum-of-subcategories product counts.
  const subTotal = (cat.subcategories || []).reduce((acc, s) => acc + (s.product_count || 0), 0);
  const unassignedCount = Math.max(0, (cat.product_count || 0) - subTotal);
  return (
    <div className="border rounded-md" data-testid={`category-row-${cat.id}`}>
      <div className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50">
        <button onClick={onToggle} className="text-slate-500 hover:text-slate-900" data-testid={`toggle-cat-${cat.id}`}>
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <FolderOpen className="h-4 w-4 text-sky-500" />
        <div className="flex-1 flex items-center gap-2 min-w-0">
          <span className={`font-medium truncate ${!cat.is_active ? 'text-slate-400 line-through' : ''}`}>
            {cat.name}
          </span>
          {cat.is_system && (
            <Badge variant="secondary" className="text-[10px] gap-1">
              <Lock className="h-3 w-3" />System
            </Badge>
          )}
          {!cat.is_active && <Badge variant="outline" className="text-[10px]">Inactive</Badge>}
          <span className="text-xs text-muted-foreground">
            {cat.product_count} product{cat.product_count === 1 ? '' : 's'} · {cat.subcategories.length} subcat
          </span>
        </div>
        <Button size="sm" variant="ghost" onClick={onAddSub} data-testid={`add-sub-${cat.id}`}>
          <Plus className="h-3.5 w-3.5 mr-1" />Sub
        </Button>
        <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`edit-cat-${cat.id}`}>
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button
          size="sm" variant="ghost"
          onClick={onDelete}
          disabled={cat.is_system}
          title={cat.is_system ? 'System category — cannot be deleted' : 'Delete'}
          className={cat.is_system ? 'opacity-40 cursor-not-allowed' : ''}
          data-testid={`delete-cat-${cat.id}`}
        >
          {cat.is_system ? <Lock className="h-3.5 w-3.5" /> : <Trash2 className="h-3.5 w-3.5" />}
        </Button>
      </div>
      {expanded && (
        <div className="border-t bg-slate-50/60">
          {cat.subcategories.length === 0 && unassignedCount === 0 ? (
            <div className="px-10 py-2 text-xs text-muted-foreground italic">No subcategories</div>
          ) : (
            <>
              {cat.subcategories.map(sub => (
                <div key={sub.id} className="flex items-center gap-2 px-3 py-1.5 pl-10 border-t border-slate-200/60"
                  data-testid={`subcategory-row-${sub.id}`}>
                  <span className={`flex-1 text-sm ${!sub.is_active ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                    {sub.name}
                  </span>
                  {!sub.is_active && <Badge variant="outline" className="text-[10px]">Inactive</Badge>}
                  <span className="text-xs text-muted-foreground mr-2">
                    {sub.product_count} product{sub.product_count === 1 ? '' : 's'}
                  </span>
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => onManageProducts(sub)}
                    disabled={sub.product_count === 0}
                    title={sub.product_count === 0 ? 'No products in this subcategory' : 'Manage / move products'}
                    data-testid={`manage-products-sub-${sub.id}`}
                  >
                    <Package className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => onEditSub(sub)} data-testid={`edit-sub-${sub.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => onDeleteSub(sub)} data-testid={`delete-sub-${sub.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              {unassignedCount > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 pl-10 border-t border-slate-200/60 bg-amber-50/40"
                  data-testid={`unassigned-row-${cat.id}`}>
                  <span className="flex-1 text-sm italic text-amber-800">
                    (Unassigned — no subcategory)
                  </span>
                  <span className="text-xs text-muted-foreground mr-2">
                    {unassignedCount} product{unassignedCount === 1 ? '' : 's'}
                  </span>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => onManageProducts(null)}
                    data-testid={`manage-products-unassigned-${cat.id}`}
                  >
                    <Package className="h-3.5 w-3.5 mr-1" />Assign to a subcategory
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Category dialog ───────────────────────────────────────────────

function CategoryDialog({ open, existing, onClose, onSave }) {
  const [name, setName] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (open) {
      setName(existing?.name || '');
      setSortOrder(existing?.sort_order ?? 0);
      setIsActive(existing?.is_active ?? true);
    }
  }, [open, existing]);

  const handleSave = () => {
    const trimmed = (name || '').trim();
    if (!trimmed) { toast.error('Name is required'); return; }
    const payload = { name: trimmed, sort_order: Number(sortOrder) || 0 };
    if (existing) payload.is_active = isActive;
    onSave(payload, existing);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{existing ? `Edit ${existing.is_system ? 'system ' : ''}category` : 'New category'}</DialogTitle>
          <DialogDescription>
            {existing?.is_system
              ? 'System categories can be renamed and reordered but cannot be deleted. Renames are display-only and do not affect historical reports.'
              : 'Create a new top-level category. Subcategories are added separately.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="cat-name">Name</Label>
            <Input id="cat-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="cat-name-input" />
          </div>
          <div>
            <Label htmlFor="cat-sort">Sort order</Label>
            <Input id="cat-sort" type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} data-testid="cat-sort-input" />
          </div>
          {existing && (
            <div className="flex items-center gap-2 pt-1">
              <Switch id="cat-active" checked={isActive} onCheckedChange={setIsActive} data-testid="cat-active-switch" />
              <Label htmlFor="cat-active" className="text-sm">Active</Label>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="cat-cancel">Cancel</Button>
          <Button onClick={handleSave} data-testid="cat-save">{existing ? 'Save' : 'Create'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Subcategory dialog ────────────────────────────────────────────

function SubcategoryDialog({ open, parentCategory, allCategories, existing, onClose, onSave }) {
  const [name, setName] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [categoryId, setCategoryId] = useState(null);
  // iter363g #4 — inventory-code auto-assign range
  const [codeMin, setCodeMin] = useState('');
  const [codeMax, setCodeMax] = useState('');

  useEffect(() => {
    if (open) {
      setName(existing?.name || '');
      setSortOrder(existing?.sort_order ?? 0);
      setIsActive(existing?.is_active ?? true);
      setCategoryId(existing?.category_id || parentCategory?.id || null);
      setCodeMin(existing?.code_min != null ? String(existing.code_min) : '');
      setCodeMax(existing?.code_max != null ? String(existing.code_max) : '');
    }
  }, [open, existing, parentCategory]);

  const handleSave = () => {
    const trimmed = (name || '').trim();
    if (!trimmed) { toast.error('Name is required'); return; }
    if (!categoryId) { toast.error('Parent category is required'); return; }
    const cmin = codeMin === '' ? 0 : parseInt(codeMin, 10);
    const cmax = codeMax === '' ? 0 : parseInt(codeMax, 10);
    if ((codeMin !== '' && Number.isNaN(cmin)) || (codeMax !== '' && Number.isNaN(cmax))) {
      toast.error('Code range must be numeric'); return;
    }
    if (cmin > 0 && cmax > 0 && cmax < cmin) {
      toast.error('Code max must be ≥ code min'); return;
    }
    const payload = {
      name: trimmed,
      sort_order: Number(sortOrder) || 0,
      category_id: Number(categoryId),
      // Send 0 sentinel to clear an existing range, otherwise the
      // actual integer. Backend treats 0 as "null".
      code_min: cmin,
      code_max: cmax,
    };
    if (existing) payload.is_active = isActive;
    onSave(payload, existing);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{existing ? 'Edit subcategory' : 'New subcategory'}</DialogTitle>
          <DialogDescription>
            Every subcategory must belong to a main category. Renaming or moving a subcategory updates all current products assigned to it; historical stocktake reports remain unchanged.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="sub-parent">Parent category {existing && <span className="text-xs text-muted-foreground">— change this to move the subcategory to another category</span>}</Label>
            <Select value={categoryId ? String(categoryId) : ''} onValueChange={(v) => setCategoryId(Number(v))}>
              <SelectTrigger id="sub-parent" data-testid="sub-parent-select">
                <SelectValue placeholder="Choose a category" />
              </SelectTrigger>
              <SelectContent>
                {(allCategories || []).filter(c => c.is_active).map(c => (
                  <SelectItem key={c.id} value={String(c.id)} data-testid={`sub-parent-option-${c.id}`}>
                    {c.name}{c.is_system ? '' : ' (custom)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="sub-name">Name</Label>
            <Input id="sub-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="sub-name-input" />
          </div>
          <div>
            <Label htmlFor="sub-sort">Sort order</Label>
            <Input id="sub-sort" type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} data-testid="sub-sort-input" />
          </div>
          {/* iter363g #4 — Inventory-code auto-assign range. When a new
              product is created under this subcategory the system
              fills `product_code` with the smallest unused integer in
              this range. Leave blank to opt out of auto-assignment. */}
          <div className="grid grid-cols-2 gap-3 pt-1 border-t">
            <div>
              <Label htmlFor="sub-code-min" className="text-xs">Inventory code — min</Label>
              <Input id="sub-code-min" type="number" min="0" placeholder="e.g. 11000"
                value={codeMin} onChange={(e) => setCodeMin(e.target.value)} data-testid="sub-code-min-input" />
            </div>
            <div>
              <Label htmlFor="sub-code-max" className="text-xs">Inventory code — max</Label>
              <Input id="sub-code-max" type="number" min="0" placeholder="e.g. 11999"
                value={codeMax} onChange={(e) => setCodeMax(e.target.value)} data-testid="sub-code-max-input" />
            </div>
            <p className="col-span-2 text-[10px] text-muted-foreground -mt-2">
              When a new product is added to this subcategory the system suggests the smallest unused code in this range. Leave blank to disable auto-assign.
            </p>
          </div>
          {existing && (
            <div className="flex items-center gap-2 pt-1">
              <Switch id="sub-active" checked={isActive} onCheckedChange={setIsActive} data-testid="sub-active-switch" />
              <Label htmlFor="sub-active" className="text-sm">Active</Label>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="sub-cancel">Cancel</Button>
          <Button onClick={handleSave} data-testid="sub-save">{existing ? 'Save' : 'Create'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ── Manage / reassign products dialog (iter354b) ─────────────────

function ManageProductsDialog({ open, source, allCategories, onClose, onDone }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [targetCatId, setTargetCatId] = useState(null);
  const [targetSubId, setTargetSubId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !source) return;
    const fetchProducts = async () => {
      setLoading(true);
      setSelected(new Set());
      setSearch('');
      setTargetCatId(null);
      setTargetSubId(null);
      try {
        const params = {
          category_id: source.sourceCategory.id,
          subcategory_id: source.sourceSubcategory ? source.sourceSubcategory.id : 0,
          page_size: 1000,
          lite: true,
          status: 'active',
          sort_by: 'product_name',
        };
        const r = await axios.get(`${API}/beverage-products`, { params, headers: authHeaders() });
        setProducts(r.data?.items || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load products');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [open, source]);

  const targetCategory = useMemo(
    () => (allCategories || []).find(c => c.id === targetCatId) || null,
    [allCategories, targetCatId]
  );
  const targetSubcategories = useMemo(
    () => (targetCategory?.subcategories || []).filter(s => s.is_active),
    [targetCategory]
  );

  const filtered = useMemo(() => {
    const term = (search || '').trim().toLowerCase();
    if (!term) return products;
    return products.filter(p =>
      (p.product_name || '').toLowerCase().includes(term) ||
      (p.product_code || '').toLowerCase().includes(term) ||
      (p.brand || '').toLowerCase().includes(term)
    );
  }, [products, search]);

  const allSelected = filtered.length > 0 && filtered.every(p => selected.has(p.id));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allSelected) filtered.forEach(p => next.delete(p.id));
    else filtered.forEach(p => next.add(p.id));
    setSelected(next);
  };

  const toggleOne = (pid) => {
    const next = new Set(selected);
    if (next.has(pid)) next.delete(pid); else next.add(pid);
    setSelected(next);
  };

  const isNoOp = (
    targetCatId === source?.sourceCategory?.id &&
    ((targetSubId || null) === (source?.sourceSubcategory?.id || null))
  );

  const targetSubName = useMemo(() => {
    if (!targetCategory) return null;
    if (!targetSubId) return `${targetCategory.name} → (no subcategory)`;
    const s = (targetCategory.subcategories || []).find(x => x.id === targetSubId);
    return s ? `${targetCategory.name} → ${s.name}` : targetCategory.name;
  }, [targetCategory, targetSubId]);

  const handleSubmit = async () => {
    if (selected.size === 0) { toast.error('Select at least one product'); return; }
    if (!targetCatId) { toast.error('Pick a target category'); return; }
    if (isNoOp) { toast.error('Target is the same as the source — nothing to move'); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/product-taxonomy/reassign-products`, {
        product_ids: Array.from(selected),
        target_category_id: targetCatId,
        target_subcategory_id: targetSubId || null,
      }, { headers: authHeaders() });
      toast.success(`Moved ${r.data?.updated ?? selected.size} product${selected.size === 1 ? '' : 's'}`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reassign failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 text-sky-600" />
            Manage products in <span className="text-sky-700">{source?.sourceLabel}</span>
          </DialogTitle>
          <DialogDescription>
            Select products, choose a destination, then move them. Historical stocktake reports are unaffected — they keep a snapshot of the original bucket.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 border rounded-md p-3 bg-slate-50">
          <div>
            <Label className="text-xs">Move to category</Label>
            <Select value={targetCatId ? String(targetCatId) : ''} onValueChange={(v) => { setTargetCatId(Number(v)); setTargetSubId(null); }}>
              <SelectTrigger data-testid="mp-target-cat">
                <SelectValue placeholder="Choose a category" />
              </SelectTrigger>
              <SelectContent>
                {(allCategories || []).filter(c => c.is_active).map(c => (
                  <SelectItem key={c.id} value={String(c.id)} data-testid={`mp-target-cat-opt-${c.id}`}>
                    {c.name}{c.is_system ? '' : ' (custom)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Subcategory (optional)</Label>
            <Select
              value={targetSubId ? String(targetSubId) : '__none__'}
              onValueChange={(v) => setTargetSubId(v === '__none__' ? null : Number(v))}
              disabled={!targetCategory}
            >
              <SelectTrigger data-testid="mp-target-sub">
                <SelectValue placeholder={targetCategory ? 'Choose subcategory (or leave blank)' : 'Pick a category first'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__" data-testid="mp-target-sub-none">— (no subcategory)</SelectItem>
                {targetSubcategories.map(s => (
                  <SelectItem key={s.id} value={String(s.id)} data-testid={`mp-target-sub-opt-${s.id}`}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-8"
              placeholder={`Search ${products.length} products in this bucket…`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="mp-search"
            />
          </div>
          <Button variant="outline" size="sm" onClick={toggleAll} disabled={filtered.length === 0}
            data-testid="mp-toggle-all">
            {allSelected ? 'Clear' : 'Select all'} ({filtered.length})
          </Button>
        </div>

        <div className="border rounded-md max-h-[300px] overflow-y-auto">
          {loading ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center" data-testid="mp-empty">
              {products.length === 0 ? 'No products in this bucket.' : 'No matches for your search.'}
            </div>
          ) : (
            filtered.map(p => {
              const isSel = selected.has(p.id);
              return (
                <button
                  key={p.id}
                  onClick={() => toggleOne(p.id)}
                  className={`w-full text-left px-3 py-2 flex items-center gap-3 border-b last:border-b-0 transition-colors ${isSel ? 'bg-sky-50' : 'hover:bg-slate-50'}`}
                  data-testid={`mp-product-${p.id}`}
                >
                  <div className={`w-4 h-4 rounded border flex items-center justify-center ${isSel ? 'bg-sky-600 border-sky-600 text-white' : 'border-slate-300'}`}>
                    {isSel && <Check className="h-3 w-3" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{p.product_name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {p.product_code || '—'}{p.brand ? ` · ${p.brand}` : ''}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        <DialogFooter className="flex-row items-center justify-between">
          <div className="text-xs text-muted-foreground" data-testid="mp-selected-count">
            {selected.size} selected
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} data-testid="mp-cancel">Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || selected.size === 0 || !targetCatId || isNoOp}
              data-testid="mp-submit"
            >
              <ArrowRightLeft className="h-4 w-4 mr-1" />
              {submitting
                ? 'Moving…'
                : `Move ${selected.size || ''}${targetSubName ? ' → ' + targetSubName : ''}`}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
